import {Button} from '@dropbox/dig-components/buttons';
import {List} from '@dropbox/dig-components/list';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Text} from '@dropbox/dig-components/typography';
import {useQuery} from '@tanstack/react-query';
import cx from 'classnames';
import {useAtom} from 'jotai';
import {Dropboxer, DropboxerId} from 'store/features/feedback_chat/types';
import {getDropboxService} from 'utilities';

import {DropboxerCard} from './dropboxer_card';
import styles from './manager_feedback.module.css';
import {spritesCycleAtom, useGetReviewCycles} from './sprites_cycle_selector';
import {TextBubble} from './text_bubble';
import {getSpriteCycleOfFeedback, isFeedbackInCycleDateRange} from './utils';

type Feedback = {
  question: string;
  htmlAnswer: string;
  date: string;
  type: string;
  feedbackGiverId: DropboxerId;
  providerTitle: string;
  providerLevel: string;
  feedbackSource?: 'peer' | 'manager' | 'additional';
};

export type FeedbackId = string;

type FeedbackState = {
  byFeedbackGiverId: {
    [feedbackGiverId: DropboxerId]: FeedbackId[];
  };
  byFeedbackId: {
    [feedbackId: FeedbackId]: Feedback;
  };
  allFeedbackIds: FeedbackId[];
};

const defaultFeedbackState: FeedbackState = {
  byFeedbackGiverId: {},
  byFeedbackId: {},
  allFeedbackIds: [],
};

type FeedbackGiversState = {
  byFeedbackGiverId: {
    [feedbackGiverId: DropboxerId]: Dropboxer;
  };
  allFeedbackGiverIds: DropboxerId[];
};

const defaultFeedbackGiversState: FeedbackGiversState = {
  byFeedbackGiverId: {},
  allFeedbackGiverIds: [],
};

const getFeedback = async (dropboxerId: DropboxerId) => {
  const feedbackResponse =
    await getDropboxService().getFeedbackApiV1DropboxerFeedbackEmployeeIdGet(
      dropboxerId
    );

  const feedbackState = feedbackResponse.feedback.reduce((prevState, fb) => {
    const feedbackGiverId = fb.feedback_giver_id;
    if (!feedbackGiverId) {
      return prevState;
    }

    const feedbackId = fb.id;

    return {
      ...prevState,
      byFeedbackGiverId: {
        ...prevState.byFeedbackGiverId,
        [feedbackGiverId]: [
          ...(prevState.byFeedbackGiverId[feedbackGiverId] || []),
          feedbackId,
        ],
      },
      byFeedbackId: {
        ...prevState.byFeedbackId,
        [feedbackId]: {
          question: fb.question,
          htmlAnswer: fb.answer,
          date: fb.date,
          type: fb.type,
          feedbackGiverId,
          providerTitle: fb.provider_title,
          providerLevel: fb.provider_level,
          feedbackSource: fb.feedback_source as
            | 'peer'
            | 'manager'
            | 'additional'
            | undefined,
        },
      },
      allFeedbackIds: [...prevState.allFeedbackIds, feedbackId],
    };
  }, defaultFeedbackState);

  const feedbackGiversState = feedbackResponse.dropboxers.reduce(
    (prevState, dropboxer) => {
      return {
        ...prevState,
        byFeedbackGiverId: {
          ...prevState.byFeedbackGiverId,
          [dropboxer.employee_id]: dropboxer,
        },
        allFeedbackGiverIds: [
          ...prevState.allFeedbackGiverIds,
          dropboxer.employee_id,
        ],
      };
    },
    defaultFeedbackGiversState
  );

  return {feedbackState, feedbackGiversState};
};

export const useGetFeedback = (dropboxerId: DropboxerId) => {
  const {data, status, error, isRefetching, refetch} = useQuery({
    queryKey: ['/dropboxer/feedback', dropboxerId],
    queryFn: () => getFeedback(dropboxerId),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  if (status === 'loading') {
    return {
      status,
    };
  }

  if (status === 'error') {
    return {
      status,
      error,
      isRefetching,
      refetch,
    };
  }

  return {
    status,
    feedbackState: data.feedbackState,
    feedbackGiversState: data.feedbackGiversState,
  };
};

interface SingleFeedbackProps {
  feedbackState: FeedbackState;
  feedbackId: string;
  feedbackGiver: Dropboxer;
}

const SingleFeedback: React.FC<SingleFeedbackProps> = ({
  feedbackState,
  feedbackId,
  feedbackGiver,
}) => {
  const {htmlAnswer, date, question, type, providerTitle, providerLevel} =
    feedbackState.byFeedbackId[feedbackId];

  const hasQuestion = question !== 'N/A';
  const hasAnswer = htmlAnswer !== 'N/A';

  if (!hasQuestion && !hasAnswer) {
    return <></>;
  }

  return (
    <div key={feedbackId}>
      <div className={styles['feedback-title']}>
        <DropboxerCard
          dropboxer={feedbackGiver}
          provider_title={providerTitle}
          provider_level={providerLevel}
        />
        <Text variant="label">{`${type} - ${date.split('T')[0]}`}</Text>
      </div>

      {hasQuestion && <TextBubble html={question} />}
      {hasAnswer && <TextBubble html={htmlAnswer} />}
    </div>
  );
};

export type SortOrder = 'asc' | 'desc';

type FeedbackProps = {
  dropboxerId: DropboxerId;
  sortOrder?: SortOrder;
  grouped?: boolean;
};

export const FeedbackController = ({
  dropboxerId,
  sortOrder = 'desc',
  grouped = true,
}: FeedbackProps) => {
  const {status, feedbackState, feedbackGiversState, isRefetching, refetch} =
    useGetFeedback(dropboxerId);

  const [spritesCycle] = useAtom(spritesCycleAtom);
  const {reviewCycles} = useGetReviewCycles();

  const isFeedbackInCycle = (type: string, date: string): boolean => {
    if (!spritesCycle) return true;
    if (reviewCycles && reviewCycles.length > 0) {
      return isFeedbackInCycleDateRange(date, spritesCycle, reviewCycles);
    }
    return getSpriteCycleOfFeedback(type, date) === spritesCycle;
  };

  if (status === 'loading') {
    return (
      <div className={cx(styles['generic-container'])}>
        <Spinner size="large" />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className={cx(styles['generic-container'])}>
        <Text variant="paragraph" tagName="p" size="xlarge">
          Error loading feedback.
        </Text>
        <Button
          variant="primary"
          onClick={() => refetch()}
          isLoading={isRefetching}
        >
          Retry
        </Button>
      </div>
    );
  }

  // Compute if there is any feedback to display for the selected cycle
  const hasAnyFeedbackForCycle =
    feedbackGiversState.allFeedbackGiverIds.reduce((total, feedbackGiverId) => {
      const allReceivedFeedbackIds =
        feedbackState.byFeedbackGiverId[feedbackGiverId];
      if (!allReceivedFeedbackIds) {
        return total;
      }
      const filteredReceivedFeedbackIds = allReceivedFeedbackIds.filter(
        (feedbackId) => {
          const {type, date} = feedbackState.byFeedbackId[feedbackId];
          return isFeedbackInCycle(type, date);
        }
      );
      return total + filteredReceivedFeedbackIds.length;
    }, 0) > 0;

  if (!hasAnyFeedbackForCycle) {
    return (
      <div className={cx(styles['generic-container'])}>
        <Text variant="paragraph" tagName="p" size="xlarge">
          No Feedback found for this Current Cycle
        </Text>
      </div>
    );
  }

  if (grouped) {
    return (
      <div className={cx(styles['feedback-container'])}>
        <List>
          {feedbackGiversState.allFeedbackGiverIds.map((feedbackGiverId) => {
            const feedbackGiver =
              feedbackGiversState.byFeedbackGiverId[feedbackGiverId];

            const allReceivedFeedbackIds =
              feedbackState.byFeedbackGiverId[feedbackGiverId];

            const filteredReceivedFeedbackIds = allReceivedFeedbackIds.filter(
              (feedbackId) => {
                const {type, date} = feedbackState.byFeedbackId[feedbackId];
                return isFeedbackInCycle(type, date);
              }
            );

            return (
              <List.Item key={feedbackGiverId} spacing="xsmall">
                <List.Content>
                  {filteredReceivedFeedbackIds.map((feedbackId) => (
                    <SingleFeedback
                      feedbackState={feedbackState}
                      feedbackId={feedbackId}
                      feedbackGiver={feedbackGiver}
                      key={feedbackId}
                    />
                  ))}
                </List.Content>
              </List.Item>
            );
          })}
        </List>
      </div>
    );
  }

  // Flat mode: flatten all feedback into a single list, filter by cycle, then sort by date
  const allFilteredFeedbackIds = feedbackState.allFeedbackIds.filter(
    (feedbackId) => {
      const {type, date} = feedbackState.byFeedbackId[feedbackId];
      return isFeedbackInCycle(type, date);
    }
  );

  const sortedFeedbackIds = [...allFilteredFeedbackIds].sort((a, b) => {
    const dateA = feedbackState.byFeedbackId[a].date;
    const dateB = feedbackState.byFeedbackId[b].date;
    return sortOrder === 'asc'
      ? dateA.localeCompare(dateB)
      : dateB.localeCompare(dateA);
  });

  return (
    <div className={cx(styles['feedback-container'])}>
      <List>
        {sortedFeedbackIds.map((feedbackId) => {
          const feedback = feedbackState.byFeedbackId[feedbackId];
          const feedbackGiver =
            feedbackGiversState.byFeedbackGiverId[feedback.feedbackGiverId];

          if (!feedbackGiver) {
            return null;
          }

          return (
            <List.Item key={feedbackId} spacing="xsmall">
              <List.Content>
                <SingleFeedback
                  feedbackState={feedbackState}
                  feedbackId={feedbackId}
                  feedbackGiver={feedbackGiver}
                />
              </List.Content>
            </List.Item>
          );
        })}
      </List>
    </div>
  );
};
